/**
 * Admin Home Components
 * A dashboard interface providing navigation to various administrative functions
 * Displays clickable cards for different administrative tasks and settings
 */

import { useNavigate } from "react-router-dom";
import { ChartBarIcon } from "@heroicons/react/24/outline";

/**
 * AdminHome Component
 * Main administrative dashboard displaying navigation cards
 *
 * Features:
 * - Quick access to key administrative functions
 * - Visual card-based navigation interface
 * - Responsive grid layout
 * - Consistent styling and iconography
 *
 * @returns {JSX.Element} A grid of navigation cards for administrative functions
 */
const AdminHome = () => {
  const navigate = useNavigate();

  /**
   * Dashboard configuration array
   * Defines the available administrative functions and their properties
   * @type {Array<{
   *   title: string,
   *   description: string,
   *   icon: Component,
   *   path: string,
   *   color: string
   * }>}
   */
  const dashboardItems = [
    {
      title: "Nuova Collezione",
      description: "Crea nuove collezioni NFT",
      icon: ChartBarIcon,
      path: "/admin/new-collection",
      color: "bg-blue-500",
    },
    {
      title: "Gestione Claim Ids",
      description: "Visualizza i claim ids delle collezioni presenti",
      icon: ChartBarIcon,
      path: "/admin/claim-ids",
      color: "bg-green-500",
    },
    {
      title: "Segnalazioni",
      description: "Visualizza e gestisci le segnalazioni",
      icon: ChartBarIcon,
      path: "/admin/reports",
      color: "bg-purple-500",
    },
    {
      title: "Impostazioni",
      description: "Configura le impostazioni della piattaforma",
      icon: ChartBarIcon,
      path: "/admin/settings",
      color: "bg-orange-500",
    },
  ];

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-8">Dashboard Amministratore</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {dashboardItems.map((item) => (
          <button
            key={item.title}
            onClick={() => navigate(item.path)}
            className="flex items-center p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow"
          >
            <div className={`p-4 rounded-lg ${item.color}`}>
              <item.icon className="h-8 w-8 text-white" />
            </div>
            <div className="ml-4 text-left">
              <h2 className="text-xl font-semibold">{item.title}</h2>
              <p className="text-gray-600 mt-1">{item.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default AdminHome;
